import { getSupportedRecorderMimeType } from '../browser-audio.util'
import { buildRecordingStreamFromState, connectTrackToActiveMixer, updateRecordingVideoComposition } from '../build-recording-stream.util'
import type { RecordingVideoCompositor } from '../compose-recording-video.util'
import type { InterviewStreamPlugin, InterviewStreamPluginContext, RecordingInitData } from '../interview-stream-host.type'
import { DEFAULT_CHUNK_DURATION_MS } from '../recording.constant'
import { waitForMediaRecorderStop } from '../recorder-flush.util'

function toBlockId(index: number): string {
    const raw = `chunk-${String(index).padStart(8, '0')}`
    return btoa(raw)
}

/** Fired once when MediaRecorder successfully starts — used to align transcripts to video t=0. */
export type RecordingStartedInfo = {
    recordingStartedAt: string
    clientNow: string
    recordingOriginMs: number
}

export type RecordingPluginHandle = {
    plugin: InterviewStreamPlugin
    initializeRecording: () => Promise<RecordingInitData>
    setInitData: (data: RecordingInitData) => void
    requestStart: (ctx: InterviewStreamPluginContext) => Promise<void>
    scheduleFallback: (delayMs: number, callback: () => void) => void
    cancelFallback: () => void
    setOnRecordingStarted: (callback: ((info: RecordingStartedInfo) => void) | null) => void
    getRecordingStartedInfo: () => RecordingStartedInfo | null
    /** Media-timeline offset in seconds since MediaRecorder.start(), or null if not recording. */
    getMediaOffsetSeconds: () => number | null
    /** Mute the local mic inside the recording mix (independent of track.enabled on the live track). */
    setLocalMuted: (muted: boolean) => void
    /** Stop MediaRecorder and wait for the last chunk upload; does not compose. */
    flushAndStop: () => Promise<void>
    stopAndFinalize: () => Promise<void>
}

export function createRecordingPlugin(): RecordingPluginHandle {
    let initData: RecordingInitData | null = null
    let started = false
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null
    let mediaRecorder: MediaRecorder | null = null
    let composedStream: MediaStream | null = null
    let videoCompositor: RecordingVideoCompositor | null = null
    let audioContext: AudioContext | null = null
    let merger: ChannelMergerNode | null = null
    let localMicGate: GainNode | null = null
    let localMicMuted = false
    let mixTracks: MediaStreamTrack[] = []
    const connectedTrackIds = new Set<string>()
    let uploadSessionId: string | null = null
    let writeBlobUrl: string | null = null
    let uploadProtocol: 'azure-blocks' | 'gcs-compose' = 'azure-blocks'
    let chunkIndex = 0
    const blockMap = new Map<number, string>()
    const pendingUploads: Promise<void>[] = []
    let ctxRef: InterviewStreamPluginContext | null = null
    let flushPromise: Promise<void> | null = null
    let recordingStartedInfo: RecordingStartedInfo | null = null
    let onRecordingStarted: ((info: RecordingStartedInfo) => void) | null = null

    const uploadChunk = async (chunk: Blob, index: number) => {
        if (!uploadSessionId || !chunk.size) {
            return
        }

        const clientToken = sessionStorage.getItem('clientToken') || ''
        let partId: string
        let putUrl: string
        let putHeaders: Record<string, string>

        if (uploadProtocol === 'gcs-compose') {
            const partRes = await fetch('/api/interviews/screening-webrtc/recording/part-url', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Token': clientToken,
                },
                body: JSON.stringify({ uploadSessionId, chunkIndex: index }),
            })
            const partJson = await partRes.json()
            const partData = partJson?.data || partJson
            if (!partRes.ok || !partData?.uploadUrl || !partData?.partId) {
                throw new Error(partJson?.error || `Failed to mint part upload URL for chunk ${index}`)
            }
            putUrl = partData.uploadUrl
            partId = partData.partId
            // Must match the Content-Type bound into the GCS V4 signed URL
            putHeaders = {
                'Content-Type': 'application/octet-stream',
            }
        } else {
            if (!writeBlobUrl) {
                return
            }
            partId = toBlockId(index)
            const separator = writeBlobUrl.includes('?') ? '&' : '?'
            putUrl = `${writeBlobUrl}${separator}comp=block&blockid=${encodeURIComponent(partId)}`
            putHeaders = {
                'x-ms-blob-type': 'BlockBlob',
                'Content-Type': chunk.type || 'application/octet-stream',
            }
        }

        const data = await chunk.arrayBuffer()
        const putRes = await fetch(putUrl, {
            method: 'PUT',
            headers: putHeaders,
            body: data,
        })
        if (!putRes.ok) {
            throw new Error(`Failed to upload recording chunk ${index}`)
        }

        blockMap.set(index, partId)
        await fetch('/api/interviews/screening-webrtc/recording/chunk', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Client-Token': clientToken,
            },
            body: JSON.stringify({
                uploadSessionId,
                chunkIndex: index,
                blockId: partId,
            }),
        })
    }

    const finalizeRecording = async () => {
        const clientToken = sessionStorage.getItem('clientToken')
        if (!clientToken || !uploadSessionId) {
            return
        }
        const orderedBlockIds = [...blockMap.entries()].sort((a, b) => a[0] - b[0]).map(([, id]) => id)
        const finalizeRes = await fetch('/api/interviews/screening-webrtc/recording/finalize', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Client-Token': clientToken,
            },
            body: JSON.stringify({
                uploadSessionId,
                blockIds: orderedBlockIds,
            }),
        })
        if (!finalizeRes.ok) {
            const json = await finalizeRes.json().catch(() => ({}))
            throw new Error(json?.error || 'Failed to finalize recording')
        }
        uploadSessionId = null
        writeBlobUrl = null
    }

    /** Tear down the compositor (its draw loop), mixer clones and AudioContext built for this recording. */
    const releaseMediaResources = () => {
        videoCompositor?.stop()
        videoCompositor = null
        mixTracks.forEach((track) => track.stop())
        mixTracks = []
        connectedTrackIds.clear()
        composedStream = null
        merger = null
        localMicGate = null
        if (audioContext) {
            audioContext.close().catch(() => undefined)
            audioContext = null
        }
    }

    const cleanupResources = () => {
        releaseMediaResources()
        started = false
        initData = null
        flushPromise = null
        recordingStartedInfo = null
        if (fallbackTimer) {
            clearTimeout(fallbackTimer)
            fallbackTimer = null
        }
    }

    const startRecorder = (stream: MediaStream, chunkDurationMs: number, log: (message: string) => void) => {
        const mimeType = getSupportedRecorderMimeType()
        const mixedAudio = stream.getAudioTracks()[0]
        const channelCount = mixedAudio?.getSettings?.()?.channelCount
        log(`Recording: MediaRecorder mime=${mimeType || 'browser-default'} audioChannels=${channelCount ?? 'unknown'}`)
        const recorder = new MediaRecorder(stream, {
            ...(mimeType ? { mimeType } : {}),
            audioBitsPerSecond: 128000,
        })
        mediaRecorder = recorder
        recorder.ondataavailable = (event) => {
            if (!event.data || event.data.size === 0) return
            const index = chunkIndex++
            const uploadTask = uploadChunk(event.data, index)
                .catch((error: unknown) => {
                    const message = error instanceof Error ? error.message : String(error)
                    log(`Chunk upload failed: ${message}`)
                })
                .finally(() => {
                    const idx = pendingUploads.indexOf(uploadTask)
                    if (idx >= 0) {
                        pendingUploads.splice(idx, 1)
                    }
                })
            pendingUploads.push(uploadTask)
        }
        recorder.onerror = (event: Event) => {
            const error = (event as Event & { error?: DOMException }).error
            log(`Recording: MediaRecorder error ${error?.name ?? 'unknown'}: ${error?.message ?? ''} (state=${recorder.state})`)
        }
        recorder.start(chunkDurationMs)
        const now = new Date()
        const info: RecordingStartedInfo = {
            recordingStartedAt: now.toISOString(),
            clientNow: now.toISOString(),
            recordingOriginMs: performance.now(),
        }
        recordingStartedInfo = info
        log(`Recording: MediaRecorder started at ${info.recordingStartedAt}`)
        onRecordingStarted?.(info)
    }

    const flushAndStop = async () => {
        if (flushPromise) {
            return flushPromise
        }

        flushPromise = (async () => {
            if (fallbackTimer) {
                clearTimeout(fallbackTimer)
                fallbackTimer = null
            }

            const recorder = mediaRecorder
            if (recorder && recorder.state !== 'inactive') {
                await waitForMediaRecorderStop(recorder)
            }
            mediaRecorder = null

            await Promise.allSettled(pendingUploads)
            pendingUploads.length = 0
        })()

        return flushPromise
    }

    const hotPlugRemoteAudio = (ctx: InterviewStreamPluginContext) => {
        if (!started || !audioContext || !merger) {
            return
        }
        const remoteTrack = ctx.streams.remoteAudioTrack
        if (!remoteTrack) {
            return
        }
        const before = connectedTrackIds.size
        connectTrackToActiveMixer(audioContext, merger, remoteTrack, mixTracks, connectedTrackIds)
        if (connectedTrackIds.size > before) {
            ctx.log('Recording: remote bot audio connected to mixer (both channels)')
        }
    }

    // Mic device switches replace streams.audioTrack mid-recording — the mixer
    // still reads the old (now-disposed) pipeline's clone, so reconnect here or
    // the candidate's channel goes silent for the rest of the session.
    const hotPlugLocalAudio = (ctx: InterviewStreamPluginContext) => {
        if (!started || !audioContext || !merger) {
            return
        }
        const micTrack = ctx.streams.audioTrack
        if (!micTrack) {
            return
        }
        const before = connectedTrackIds.size
        connectTrackToActiveMixer(audioContext, merger, micTrack, mixTracks, connectedTrackIds, localMicGate)
        if (connectedTrackIds.size > before) {
            ctx.log('Recording: local mic track reconnected to mixer')
        }
    }

    const applyInitData = (data: RecordingInitData) => {
        initData = data
        uploadSessionId = data.uploadSessionId
        writeBlobUrl = data.blobWriteUrl || null
        uploadProtocol = data.uploadProtocol || (data.blobWriteUrl ? 'azure-blocks' : 'gcs-compose')
        chunkIndex = 0
        blockMap.clear()
    }

    const plugin: InterviewStreamPlugin = {
        id: 'recording',
        start(ctx) {
            ctxRef = ctx
        },
        stop() {
            ctxRef = null
        },
        onStreamsUpdated(ctx) {
            ctxRef = ctx
            if (!started || !composedStream) {
                return
            }
            if (updateRecordingVideoComposition(videoCompositor, ctx.streams)) {
                ctx.log('Recording: video sources updated')
            }
            hotPlugLocalAudio(ctx)
            hotPlugRemoteAudio(ctx)
        },
    }

    return {
        plugin,
        async initializeRecording(): Promise<RecordingInitData> {
            const clientToken = sessionStorage.getItem('clientToken')
            if (!clientToken) {
                throw new Error('Missing client token for recording init')
            }
            const initRes = await fetch('/api/interviews/screening-webrtc/recording/init', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Token': clientToken,
                },
                body: JSON.stringify({}),
            })
            const initJson = await initRes.json()
            const data = (initJson?.data || initJson) as RecordingInitData
            const protocol = data.uploadProtocol || (data.blobWriteUrl ? 'azure-blocks' : 'gcs-compose')
            if (!initRes.ok || !data.uploadSessionId || (protocol === 'azure-blocks' && !data.blobWriteUrl)) {
                throw new Error(initJson?.error || 'Failed to initialize recording')
            }
            applyInitData({ ...data, uploadProtocol: protocol })
            return data
        },
        setInitData(data: RecordingInitData) {
            applyInitData(data)
        },
        scheduleFallback(delayMs: number, callback: () => void) {
            if (fallbackTimer) {
                clearTimeout(fallbackTimer)
            }
            fallbackTimer = setTimeout(callback, delayMs)
        },
        cancelFallback() {
            if (fallbackTimer) {
                clearTimeout(fallbackTimer)
                fallbackTimer = null
            }
        },
        setOnRecordingStarted(callback: ((info: RecordingStartedInfo) => void) | null) {
            onRecordingStarted = callback
            if (callback && recordingStartedInfo) {
                callback(recordingStartedInfo)
            }
        },
        getRecordingStartedInfo() {
            return recordingStartedInfo
        },
        getMediaOffsetSeconds() {
            if (!recordingStartedInfo) {
                return null
            }
            return Math.max(0, (performance.now() - recordingStartedInfo.recordingOriginMs) / 1000)
        },
        async requestStart(ctx: InterviewStreamPluginContext) {
            if (started) {
                return
            }
            const hasMedia = ctx.streams.audioTrack || ctx.streams.activeVideoTrack
            if (!initData || !hasMedia) {
                ctx.log(`Recording: not starting (init=${Boolean(initData)} media=${Boolean(hasMedia)})`)
                return
            }

            if (fallbackTimer) {
                clearTimeout(fallbackTimer)
                fallbackTimer = null
            }

            started = true
            const hasRemote = Boolean(ctx.streams.remoteAudioTrack)
            ctx.log(`Recording: starting (mic${hasRemote ? ' + bot' : ''} + camera)`)

            const resources = await buildRecordingStreamFromState(ctx.streams, connectedTrackIds)
            if (!resources) {
                started = false
                connectedTrackIds.clear()
                return
            }

            audioContext = resources.audioContext
            merger = resources.merger
            localMicGate = resources.localMicGate
            localMicGate.gain.value = localMicMuted ? 0 : 1
            mixTracks = resources.mixTracks
            composedStream = resources.composedStream
            videoCompositor = resources.compositor
            try {
                startRecorder(resources.composedStream, initData.chunkDurationMs || DEFAULT_CHUNK_DURATION_MS, ctx.log)
                hotPlugRemoteAudio(ctx)
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : String(error)
                ctx.log(`Recording: MediaRecorder failed to start: ${message}`)
                // Without this the compositor's draw loop and the AudioContext keep running,
                // and every later requestStart retry stacks another set.
                releaseMediaResources()
                started = false
                mediaRecorder = null
            }
        },
        flushAndStop,
        setLocalMuted(muted: boolean) {
            localMicMuted = muted
            // Short ramp avoids a click in the recording, same idiom as mic ducking.
            if (localMicGate && audioContext && audioContext.state !== 'closed') {
                localMicGate.gain.setTargetAtTime(muted ? 0 : 1, audioContext.currentTime, 0.03)
            }
        },
        async stopAndFinalize() {
            await flushAndStop()

            if (blockMap.size === 0) {
                ctxRef?.log('Recording: skipping finalize (no chunks uploaded)')
                cleanupResources()
                return
            }

            try {
                await finalizeRecording()
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : String(error)
                ctxRef?.log(`Finalize failed: ${message}`)
            }

            cleanupResources()
        },
    }
}
